import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from './Header';
import BundleNetworks from './BundleNetworks';
import HoldersTable from './HoldersTable';
import FigureBadge from './FigureBadge';
import Footer from './Footer';
import SocialMetaTags from './SocialMetaTags';
import RefreshModal from './RefreshModal';
import { generateSocialMediaMessage } from '../utils/riskUtils';
import { apiFetch, getApiHost } from '../utils/api';
import StreamflowLocks from './StreamflowLocks';
import TibaneStaking from './TibaneStaking';
import TierGate from './TierGate';
import { useAuth } from '../contexts/AuthContext';
import { hasWatcherAccess } from '../utils/tiers';
import { trackEvent } from '../utils/analytics';
import AddressLink from './AddressLink';
import { EVM_CHAIN_INFO, EvmChainId, holderAnalysisPath, tokenPath } from '../utils/chains';

interface HolderAnalysisDetails {
  mint: string;
  name: string;
  symbol: string;
  uri: string;
  uriMetadata: {
    name: string;
    symbol: string;
    image: string;
    description: string;
    tags: string[];
    extensions: {
      website: string;
      twitter: string;
      telegram: string;
    };
    website: string;
    twitter: string;
    telegram: string;
  };
  creator: string;
  creatorFigure?: { figureKey: string; displayName: string; trustLevel: string | null; confidence?: 'confirmed' | 'probable' } | null;
  creatorNetwork: string;
  createdAt: string;
  platform?: string;
  decimals?: number;
  networkPercentage?: string;
  riskLevel?: string;
  networkBundles?: Array<{
    address: string;
    name: string;
    percentOwnership: number;
    riskLevel: string;
    accounts: Array<{
      walletAddress: string;
      tokenAmount: string;
      percentage: number;
      topFundingSource: string;
      tokenAccountAddress: string;
      oldestTx: string;
      riskLevel: string;
      label: string;
      tags: string[];
      exchange?: string | null;
    }>;
  }>;
  individualHolders?: Array<{
    walletAddress: string;
    tokenAmount: string;
    percentage: number;
    topFundingSource: string;
    tokenAccountAddress: string;
    oldestTx: string;
    riskLevel: string;
    exchange: string;
  }>;
  uniqueWalletCount?: number;
  totalAccounts?: number;
  totalHolders?: number;
  topHolderOwnership?: number;
  topHolderRiskLevel?: 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW';
  topTenOwnership?: number;
  topTenHoldersRiskLevel?: 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW';
  topNetworkOwnership?: number;
  walletsAnalyzed?: number;
  percentageOfHoldingsAnalyzed?: number;
  averageWalletAge?: number;
  ruggerHoldingsPct?: number | null;
  timestamp?: string;
  availableTimestamps?: string[];
  exchangeDistributionWarning?: string;
  overallSafetyLevel?: string;
  criticalRisks?: string[];
  warnings?: string[];
  hasHolderAnalysis: boolean;
  hasSocialResearch?: boolean;
}

// Match V2 token-details page: HIGH/EXTREME -> red, MEDIUM -> yellow.
const statColor = (level: string | null | undefined): string | undefined => {
  if (level === 'HIGH' || level === 'EXTREME') return 'text-crypto-red';
  if (level === 'MEDIUM') return 'text-crypto-yellow';
  return undefined;
};

const formatWalletAge = (days: number | null | undefined): string => {
  if (days == null) return '—';
  if (days < 60) return `${Math.round(days)}d`;
  if (days < 730) return `${Math.round(days / 30.4)}mo`;
  return `${(days / 365.25).toFixed(1)}y`;
};

// Mirror of the worker's EXCHANGE_BASELINE: retail-base funding shares of
// genuinely massively-distributed coins (>=80k holders, top-10 <=35%), each
// computed over ALL researched wallets (individuals + funding-network members,
// Solana only; regenerated 2026-10-08) after dropping risk-exempt + top-5. Keep in sync with
// deepnets_worker/src/backend/holder-providers/holder-research.ts —
// regenerate both with scripts/gen-final.mjs.
const EXCHANGE_BASELINE: Record<string, number> = {
  'Binance': 0.3345, 'Coinbase': 0.1893, 'FTX': 0.1193, 'Kraken': 0.1116, 
  'OKX': 0.0478, 'Bybit': 0.0396, 'KuCoin': 0.0255, 'MEXC': 0.0163, 
  'Huobi': 0.0162, 'Gate.io': 0.014, 'Mayan Finance': 0.0095, 'Crypto.com': 0.0091, 
  'Relay.link': 0.0084, 'Bitget': 0.0075, 'Bitvavo': 0.0055, 'Robinhood': 0.0048, 
  'Moonpay': 0.0048, 'Bitstamp': 0.0045, 'HTX': 0.004, 'Revolut': 0.004, 
  'Debridge Finance': 0.0034, 'Backpack': 0.0031, 'SwissBorg': 0.0027, 'FixedFloat Exchange': 0.0022, 
  'ChangeNow Exchange': 0.0021, 'Hyperunit': 0.0019, 'Change Hero': 0.0017, 'FalconX': 0.0015, 
  'CoinEx': 0.0014, 'Bitfinex': 0.0011, 'Genesis Account': 0.0008, 'Rollbit': 0.0006, 
  'LBank': 0.0005, 'CoinWx': 0.0004, 
};

// Anomaly chart for exchange funding distribution. Each row is one
// exchange; the centered bar shows how many percentage points its
// share of exchange-funded holdings differs from the baseline. Bars
// to the right = over-represented (suspicious if large), to the left
// = under-represented. Rows are sorted by actual presence so reading
// down the list matches "who funded this token, largest to smallest".
//
// We count every analyzed holder that has an exchange tag — both
// `individualHolders` (wallets directly funded by a curated exchange,
// so each is its own "network of 1") AND `networkBundles[].accounts[]`
// (wallets inside a funding-network cluster whose root traces up to a
// curated exchange — every member inherits the root's exchange tag).
// A token where 200 wallets all live in a single OKX-funded network
// then shows up here as +200 OKX votes — letting the anomaly chart
// fire even when the token's holder set is dominated by one cluster.
const ExchangeAnomalyChart: React.FC<{
  individualHolders: Array<{ exchange?: string | null; percentage?: number }>;
  networkBundles?: Array<{ accounts: Array<{ exchange?: string | null; percentage?: number }> }>;
  warning?: string;
}> = ({ individualHolders, networkBundles, warning }) => {
  // Group every exchange-funded holder by exchange. Holders without an
  // exchange label are ignored (they'd otherwise distort the share
  // denominator we use for baseline comparison).
  const byExchange = new Map<string, { pct: number; walletCount: number }>();
  const tallyHolder = (h: { exchange?: string | null; percentage?: number }) => {
    if (!h.exchange) return;
    const entry = byExchange.get(h.exchange) || { pct: 0, walletCount: 0 };
    entry.pct += h.percentage ?? 0;
    entry.walletCount += 1;
    byExchange.set(h.exchange, entry);
  };
  // Match the worker's scorer: measure the RETAIL funding base by excluding the
  // top-5 holders (whales / exchange-custody / a KOL's single funded wallet
  // distort the picture — a Coinbase-funded 58% holder made $ANSEM read as "88%
  // Coinbase"). Rank the combined holder set and drop the largest 5.
  const allHolders = [
    ...individualHolders,
    ...(networkBundles ?? []).flatMap(b => b.accounts),
  ].sort((a, b) => (b.percentage ?? 0) - (a.percentage ?? 0));
  for (const h of allHolders.slice(5)) tallyHolder(h);

  const totalExchPct = Array.from(byExchange.values()).reduce((s, e) => s + e.pct, 0);
  const totalExchWallets = Array.from(byExchange.values()).reduce((s, e) => s + e.walletCount, 0);

  if (totalExchPct < 1 || totalExchWallets < 5) {
    return (
      <div className="bg-crypto-card border border-gray-800 rounded-xl p-3 text-sm text-gray-500">
        Too few exchange-funded wallets to compare against baseline ({totalExchWallets} found).
      </div>
    );
  }

  // Build deltas across the union of (baseline keys ∪ actual keys).
  const allKeys = new Set([...Object.keys(EXCHANGE_BASELINE), ...byExchange.keys()]);
  const actualShare: Record<string, number> = {};
  for (const [name, entry] of byExchange) {
    actualShare[name] = entry.pct / totalExchPct;
  }

  const deltas = Array.from(allKeys).map(name => {
    const actual = actualShare[name] ?? 0;
    const baseline = EXCHANGE_BASELINE[name] ?? 0;
    return {
      name,
      actualPp: actual * 100,        // share-of-exchange-funded, in percentage points
      baselinePp: baseline * 100,
      deltaPp: (actual - baseline) * 100,
    };
  })
  // Keep exchanges that are meaningfully present (any real share) OR
  // meaningfully absent when baseline expected them (big negative
  // delta). This keeps the chart readable without dropping the
  // "expected but missing" anomaly signal.
  .filter(d => d.actualPp >= 0.5 || d.deltaPp <= -3)
  // Primary sort: actual presence DESC, so row order matches "who's
  // funding this token, largest to smallest". Secondary sort: |delta|
  // DESC, so among the expected-but-absent (actualPp=0) tail the
  // bigger anomalies show first.
  .sort((a, b) => (b.actualPp - a.actualPp) || (Math.abs(b.deltaPp) - Math.abs(a.deltaPp)))
  .slice(0, 12);

  // Visual scale: cap bar at ±30pp; anything beyond is the "shouting" zone.
  const VISUAL_CAP = 30;
  const ANOMALY_THRESHOLD = 5;

  // Verdict comes from worker's stored warning (text already explains
  // why it's unusual) or default to organic if no warning.
  const isUnusual = !!warning;

  return (
    <div className="bg-crypto-card border border-gray-800 rounded-xl p-3">
      <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <i className="fas fa-balance-scale text-blue-400" />
          Funding source vs baseline
        </h3>
        <span className={`text-xs font-semibold uppercase tracking-wider ${isUnusual ? 'text-crypto-yellow' : 'text-crypto-accent'}`}>
          {isUnusual ? '⚠ Unusual' : '✓ Looks organic'}
        </span>
      </div>
      {warning && (
        <p className="text-xs text-yellow-300 bg-yellow-900/20 border border-yellow-700/40 rounded px-2 py-1.5 mb-3">
          {warning}
        </p>
      )}
      {!warning && (
        <p className="text-xs text-gray-500 mb-3">
          Each row shows how this token's exchange-funding distribution differs from the typical organic Solana token. Wider bars = more anomalous.
        </p>
      )}
      <div className="space-y-1">
        {deltas.map(d => {
          const isOver = d.deltaPp > 0;
          const isAnomaly = Math.abs(d.deltaPp) >= ANOMALY_THRESHOLD;
          const magnitude = Math.min(Math.abs(d.deltaPp), VISUAL_CAP);
          const widthPct = (magnitude / VISUAL_CAP) * 50; // each side gets up to 50% of bar width
          const fillColor = isOver
            ? (isAnomaly ? 'bg-red-500/70' : 'bg-red-500/30')
            : (isAnomaly ? 'bg-sky-500/60' : 'bg-sky-500/25');
          const deltaTextColor = isAnomaly
            ? (isOver ? 'text-red-300' : 'text-sky-300')
            : 'text-gray-500';
          return (
            <div key={d.name} className="grid grid-cols-[110px_44px_minmax(0,1fr)_70px] items-center gap-2 text-xs">
              <span className="text-gray-300 truncate" title={`Actual ${d.actualPp.toFixed(1)}% · Baseline ${d.baselinePp.toFixed(1)}%`}>
                {d.name}
              </span>
              <span className="text-right tabular-nums text-gray-400" title={`baseline ${d.baselinePp.toFixed(1)}%`}>
                {d.actualPp.toFixed(0)}%
              </span>
              <div className="relative h-4 bg-crypto-gray/40 rounded">
                <div className="absolute top-0 bottom-0 w-px left-1/2 bg-gray-600" />
                {isOver ? (
                  <div className={`absolute top-0 bottom-0 left-1/2 ${fillColor} rounded-r`} style={{ width: `${widthPct}%` }} />
                ) : (
                  <div className={`absolute top-0 bottom-0 right-1/2 ${fillColor} rounded-l`} style={{ width: `${widthPct}%` }} />
                )}
              </div>
              <span className={`text-right tabular-nums font-medium ${deltaTextColor}`}>
                {isOver ? '+' : ''}{d.deltaPp.toFixed(0)}pp
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-2 border-t border-gray-800 text-[11px] text-gray-500 flex items-center gap-3 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-red-500/70" /> over-represented</span>
        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm bg-sky-500/60" /> under-represented</span>
        <span className="text-gray-600">·</span>
        <span>{totalExchWallets} exchange-funded wallets analyzed</span>
      </div>
    </div>
  );
};

// Same StatCard shape as V2 hero — small rectangular tile with a big
// value on top, uppercase label below, optional hover arrow when
// rendered as a Link. Colours the value if a risk level dictates it.
const StatCard: React.FC<{ label: string; value: React.ReactNode; valueColor?: string; to?: string }> = ({ label, value, valueColor, to }) => {
  const body = (
    <>
      <div className={`text-base font-semibold leading-tight ${valueColor ?? 'text-white'}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5 flex items-center justify-between">
        <span>{label}</span>
        {to && <i className="fas fa-arrow-right text-[9px] opacity-0 group-hover:opacity-60 transition-opacity" />}
      </div>
    </>
  );
  if (to) {
    return (
      <Link to={to} className="group bg-crypto-card border border-gray-800 hover:border-gray-600 rounded-xl px-3 py-2 transition-colors">
        {body}
      </Link>
    );
  }
  return (
    <div className="bg-crypto-card border border-gray-800 rounded-xl px-3 py-2">
      {body}
    </div>
  );
};

function HolderAnalysisPage({ chain }: { chain?: EvmChainId }) {
  // Outer wrapper progressively asks for access. Lets Content mount once
  // EITHER the user has Watcher+ OR has burned a credit to unlock this
  // mint. Keeps Rules of Hooks happy by deferring all data-fetching hooks
  // to the Content component.
  const { mint } = useParams<{ mint: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [unlockChecked, setUnlockChecked] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !mint) { setUnlockChecked(true); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(`${getApiHost()}/api/research-unlock/${mint}`);
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setUnlocked(!!data.unlocked);
        }
      } finally {
        if (!cancelled) setUnlockChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [authLoading, isAuthenticated, mint]);

  if (authLoading || (isAuthenticated && !unlockChecked)) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-crypto-accent border-t-transparent rounded-full" />
        </main>
        <Footer />
      </div>
    );
  }
  if (!hasWatcherAccess(user?.tier) && !unlocked) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
        <Header />
        <main className="flex-1">
          <TierGate
            required="watcher"
            feature="full holder details"
            mint={mint}
            onUnlock={() => setUnlocked(true)}
          />
        </main>
        <Footer />
      </div>
    );
  }
  return <HolderAnalysisPageContent chain={chain} />;
}

function HolderAnalysisPageContent({ chain }: { chain?: EvmChainId }) {
  const { mint, timestamp } = useParams<{ mint: string; timestamp?: string }>();
  const [tokenDetails, setTokenDetails] = useState<HolderAnalysisDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [autoRequested, setAutoRequested] = useState(false);

  useEffect(() => {
    if (mint) {
      // For /holder-analysis/:mint/latest route, timestamp is undefined, so use 'latest'
      // For /holder-analysis/:mint/:timestamp route, timestamp has a value
      // For /holder-analysis/:mint route, we need to redirect to /latest
      if (!timestamp) {
        const currentPath = window.location.pathname;
        if (currentPath === `/holder-analysis/${mint}` || (chain && currentPath === `/holder-analysis/${chain}/${mint}`)) {
          // This is the old route /holder-analysis/:mint, redirect to /holder-analysis/:mint/latest
          window.location.replace(holderAnalysisPath(mint, chain));
          return;
        }
        // This is /holder-analysis/:mint/latest route, use 'latest'
        fetchTokenDetails(mint, 'latest');
      } else {
        fetchTokenDetails(mint, timestamp);
      }
    }
  }, [mint, timestamp, chain]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTokenDetails = async (tokenMint: string, analysisParam: string) => {
    try {
      setLoading(true);
      const response = await apiFetch(`${getApiHost()}/api/token-details/${tokenMint}/${analysisParam}${chain ? `?chain=${chain}` : ''}`);
      if (!response.ok) {
        throw new Error('Failed to fetch token details');
      }
      const data = await response.json();
      setTokenDetails(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // `trigger` matters: this fires both from the button AND automatically from the
  // effect below whenever a token has no analysis yet (search sends unanalysed
  // mints straight here). Without the distinction the metric would be almost
  // entirely auto-requests and say nothing about intent.
  const requestHolderAnalysis = useCallback(async (trigger: 'manual' | 'auto' = 'manual') => {
    if (!tokenDetails) return;

    trackEvent('holder_analysis_requested', { trigger, mint: tokenDetails.mint });
    try {
      const token = localStorage.getItem('deepnets_token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await apiFetch(`${getApiHost()}/api/request-token-safety-analysis`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ mint: tokenDetails.mint, ...(chain ? { chain } : {}) }),
      });

      if (!response.ok) {
        trackEvent('holder_analysis_request_failed', { trigger, status: response.status });
        throw new Error(`Failed to request analysis (${response.status})`);
      }

      const data = await response.json();
      setCurrentRequestId(data.requestId);
      setRefreshModalOpen(true);
    } catch (error) {
      console.error('Error requesting analysis:', error);
      trackEvent('holder_analysis_request_failed', { trigger, status: 0 });
      // Only interrupt when the user actually asked for this. The auto path fires
      // for anyone landing on an unresearched mint, and the endpoint requires
      // auth — so an anonymous visitor was getting a browser alert() reading
      // "Failed to request analysis" for something they never requested. The
      // page already renders its own empty state for that case.
      if (trigger === 'manual') alert('Failed to request analysis. Please try again.');
    }
  }, [tokenDetails, chain]);

  // Automatically request analysis when token has no holder analysis or when token details fail to load
  useEffect(() => {
    if (!autoRequested && !loading) {
      // If we have token details and no analysis, request it
      if (tokenDetails && !tokenDetails.hasHolderAnalysis) {
        setAutoRequested(true);
        requestHolderAnalysis('auto');
      }
      // If token details failed to load (token might not exist yet), also try to request analysis
      else if (error && mint) {
        setAutoRequested(true);
        // Create a temporary tokenDetails object for the request
        const tempTokenDetails = { mint };
        setTokenDetails(tempTokenDetails as any);
        requestHolderAnalysis('auto');
      }
    }
  }, [tokenDetails, autoRequested, loading, error, requestHolderAnalysis, mint]);

  // Reset autoRequested when token changes
  useEffect(() => {
    setAutoRequested(false);
  }, [mint]);

  const handleRefreshSuccess = () => {
    // Refresh the page to show updated analysis
    window.location.reload();
  };

  const handleRefreshError = (errorMessage: string) => {
    // Show error message
    alert(`Analysis failed: ${errorMessage}`);
  };

  const handleModalClose = () => {
    setRefreshModalOpen(false);
    setCurrentRequestId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white">
        <Header />
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="text-gray-400">Loading token details...</div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error && !tokenDetails) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white">
        <Header />
        <main className="max-w-7xl mx-auto px-6 py-8">
          <div className="text-center py-12">
            <div className="text-red-400 mb-4">
              <i className="fas fa-exclamation-triangle text-4xl"></i>
            </div>
            <p className="text-gray-400 mb-4">Failed to load token details: {error}</p>
            <p className="text-gray-500 mb-6">This token may not exist yet or there may be an issue with our systems.</p>
            <button
              onClick={() => {
                // Create a temporary tokenDetails object for the request
                const tempTokenDetails = { mint: mint || '', hasHolderAnalysis: false };
                setTokenDetails(tempTokenDetails as any);
                setError(null); // Clear error to allow the analysis UI to show
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              <i className="fas fa-search mr-2"></i>
              Try Requesting Analysis Anyway
            </button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // At this point, tokenDetails should not be null due to the early returns above
  if (!tokenDetails) {
    return null; // This should never happen, but TypeScript needs it
  }

  // Generate social media meta tags with fallbacks for incomplete token data.
  //
  // Everything we hand outwards — the X share, the copy-link button, and og:url —
  // points at /token/:mint, NOT at this page. Holder analysis sits behind a
  // TierGate ("full holder details"), so sharing this URL sent people to a locked
  // page: a bad first impression for exactly the visitor a share is meant to win.
  // /token/:mint is public, carries the same headline verdict, and links back
  // here for anyone with access. It also degrades gracefully for an unresearched
  // mint (auto-runs a scan rather than 404ing), and the share controls below are
  // gated on hasHolderAnalysis anyway, so a shared token always has data.
  const shareUrl = `${window.location.origin}${tokenPath(tokenDetails.mint, chain)}`;
  const tokenName = tokenDetails.name || 'Unknown Token';
  const tokenSymbol = tokenDetails.symbol || tokenDetails.mint?.slice(0, 8) || 'TOKEN';
  const title = `${tokenName} (${tokenSymbol}) - Holder Analysis | Deepnets.ai`;
  const description = tokenDetails.hasHolderAnalysis
    ? `Analyze ${tokenName} (${tokenSymbol}) token details, holder distribution, risk assessment, and bundle networks. Created by ${tokenDetails.creatorNetwork || 'Unknown'} with ${tokenDetails.networkPercentage}% bundle ownership.`
    : `View ${tokenName} (${tokenSymbol}) token details. Holder analysis not yet available.`;
  const image = tokenDetails.uriMetadata?.image || tokenDetails.uri || `${window.location.origin}/logo192.png`; // Use uriMetadata image if available

  // Social sharing functions - only available when we have holder analysis
  const shareOnTwitter = () => {
    if (!tokenDetails.hasHolderAnalysis) return;
    const text = generateSocialMediaMessage(tokenDetails as any); // Type assertion needed due to interface mismatch
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      // You could add a toast notification here
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const formatTimeAgo = (dateString: string) => {
    let date: Date;

    // Try parsing as ISO string first (for createdAt)
    const isoDate = new Date(dateString);
    if (!isNaN(isoDate.getTime())) {
      date = isoDate;
    } else {
      // Try parsing as epoch timestamp (for analysis timestamps)
      const timestamp = parseInt(dateString);
      date = !isNaN(timestamp) && timestamp > 0 ? new Date(timestamp) : new Date();
    }

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes}m ago`;
    } else {
      return 'now';
    }
  };

  const navigateToAnalysis = (timestamp: string) => {
    window.location.href = holderAnalysisPath(tokenDetails.mint, chain, timestamp);
  };

  const getCurrentTimestampIndex = () => {
    if (!tokenDetails.availableTimestamps || !tokenDetails.timestamp) return -1;
    return tokenDetails.availableTimestamps.findIndex(ts => ts === tokenDetails.timestamp);
  };

  const navigateOlder = () => {
    const currentIndex = getCurrentTimestampIndex();
    if (currentIndex >= 0 && currentIndex < tokenDetails.availableTimestamps!.length - 1) {
      navigateToAnalysis(tokenDetails.availableTimestamps![currentIndex + 1]);
    }
  };

  const navigateNewer = () => {
    const currentIndex = getCurrentTimestampIndex();
    if (currentIndex > 0) {
      navigateToAnalysis(tokenDetails.availableTimestamps![currentIndex - 1]);
    }
  };

  const imageUrl = tokenDetails.uriMetadata?.image || null;
  const topNetworkBundler = tokenDetails.networkBundles && tokenDetails.networkBundles.length > 0 ? tokenDetails.networkBundles[0] : null;
  const networkGroupCount = tokenDetails.networkBundles?.length ?? 0;
  const timestampIndex = getCurrentTimestampIndex();
  const hasPrev = tokenDetails.availableTimestamps && timestampIndex < tokenDetails.availableTimestamps.length - 1;
  const hasNext = tokenDetails.availableTimestamps && timestampIndex > 0;

  return (
    <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
      <SocialMetaTags
        title={title}
        description={description}
        image={image}
        url={shareUrl}
        type="article"
      />
      <Header />
      <main className="max-w-[1600px] w-full mx-auto px-4 py-3 space-y-2 flex-1">
        <Link
          to={tokenPath(tokenDetails.mint, chain)}
          className="text-sm text-gray-400 hover:text-crypto-accent transition-colors inline-flex items-center gap-1.5"
        >
          <i className="fas fa-arrow-left text-xs" />
          Back to Token Details
        </Link>

        {/* HERO — slim. Identity (image + symbol + name + page tag)
            and the analysis-snapshot strip (timestamp/nav/refresh/
            share). Mint, creator, social icons and description all
            already live on the token-details page; not repeated here. */}
        <div className="bg-crypto-card border border-gray-800 rounded-xl p-3">
          <div className="flex items-center gap-3 flex-wrap">
            {imageUrl ? (
              <img src={imageUrl} alt={tokenDetails.symbol} className="w-10 h-10 rounded-full flex-shrink-0 bg-crypto-gray object-cover" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            ) : (
              <div className="w-10 h-10 rounded-full bg-crypto-gray flex items-center justify-center text-gray-500 font-bold flex-shrink-0">{tokenDetails.symbol?.[0] ?? '?'}</div>
            )}
            <div className="flex items-baseline gap-2 flex-wrap min-w-0">
              <h1 className="text-xl font-bold leading-tight">${tokenDetails.symbol}</h1>
              <span className="text-gray-400 text-sm truncate">{tokenDetails.name}</span>
              <span className="text-[10px] font-semibold text-purple-300 bg-purple-500/10 border border-purple-500/30 px-1.5 py-0.5 rounded uppercase tracking-wide">Holder Analysis</span>
              {chain && <span className={`text-[10px] font-semibold border px-1.5 py-0.5 rounded ${EVM_CHAIN_INFO[chain].badgeClass}`}>{EVM_CHAIN_INFO[chain].short}</span>}
            </div>
          </div>
          {tokenDetails.creatorFigure && (
            <div className="mt-1.5 flex items-center gap-2 text-[12px] text-gray-500">
              <i className="fas fa-hammer text-[10px]" /> Created by
              <FigureBadge figureKey={tokenDetails.creatorFigure.figureKey} displayName={tokenDetails.creatorFigure.displayName} trustLevel={tokenDetails.creatorFigure.trustLevel} confidence={tokenDetails.creatorFigure.confidence} className="text-[12px]" />
            </div>
          )}
          {tokenDetails.timestamp && (
            <div className="mt-2 pt-2 border-t border-gray-800 flex items-center flex-wrap gap-2 text-[12px]">
              <span className="flex items-center gap-1.5 text-gray-500">
                <i className="fas fa-clock" />
                Analysis from <span className="font-medium">{formatTimeAgo(tokenDetails.timestamp)}</span>
              </span>
              {tokenDetails.availableTimestamps && tokenDetails.availableTimestamps.length > 1 && (
                <span className="flex items-center gap-1 ml-1">
                  <button onClick={navigateOlder} disabled={!hasPrev} className="px-1.5 py-0.5 rounded border border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed" title="Older analysis">
                    <i className="fas fa-chevron-left text-[10px]" />
                  </button>
                  <button onClick={navigateNewer} disabled={!hasNext} className="px-1.5 py-0.5 rounded border border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed" title="Newer analysis">
                    <i className="fas fa-chevron-right text-[10px]" />
                  </button>
                </span>
              )}
              {tokenDetails.hasHolderAnalysis && (
                <button
                  onClick={() => requestHolderAnalysis('manual')}
                  className="px-2.5 py-1 font-medium rounded-lg border border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600 transition"
                  title={tokenDetails.hasSocialResearch ? 'Re-run safety + social research (costs 1 credit)' : 'Run full research incl. social data (costs 1 credit)'}
                >
                  {tokenDetails.hasSocialResearch ? 'Refresh (1 credit)' : 'Full Research (1 credit)'}
                </button>
              )}
              <button
                onClick={shareOnTwitter}
                disabled={!tokenDetails.hasHolderAnalysis}
                className="flex items-center gap-1.5 px-2.5 py-1 font-medium rounded-lg border border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600 disabled:opacity-40 transition"
                title="Share on X"
              >
                <i className="fab fa-x-twitter" /><span>Share</span>
              </button>
              <button
                onClick={copyToClipboard}
                className="flex items-center gap-1.5 px-2.5 py-1 font-medium rounded-lg border border-gray-700 text-gray-500 hover:text-gray-300 hover:border-gray-600 transition"
                title="Copy page link"
              >
                <i className="fas fa-link" />
              </button>
            </div>
          )}
        </div>

        {!tokenDetails.hasHolderAnalysis ? (
          <div className="bg-crypto-card border border-gray-800 rounded-xl p-6 flex flex-col items-center text-center">
            <i className="fas fa-chart-line text-4xl text-gray-600 mb-3" />
            <p className="text-base mb-1">No recent holder analysis</p>
            <p className="text-sm text-gray-500 mb-4">We don't have a recent analysis for this token.</p>
            <button
              onClick={() => requestHolderAnalysis('manual')}
              className="bg-crypto-accent/10 hover:bg-crypto-accent/20 text-crypto-accent px-5 py-2 rounded-lg font-medium border border-crypto-accent/40 transition-colors"
            >
              <i className="fas fa-search mr-2" />
              Request holder analysis
            </button>
          </div>
        ) : (
          <>
            {/* Top network bundler — promoted to its own full-width
                callout since it's the most meaningful single insight
                when a concentrated network exists. */}
            {topNetworkBundler && (() => {
              const wc = topNetworkBundler.accounts?.length ?? 0;
              const pct = topNetworkBundler.percentOwnership ?? 0;
              return (
                <div className="bg-crypto-card border border-gray-800 rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    <i className="fas fa-network-wired text-purple-400" />
                    <span className="text-gray-400">Top network bundler:</span>
                    <AddressLink address={topNetworkBundler.address} chain={chain} kind="network" className="font-semibold text-crypto-accent hover:text-crypto-accent/80 truncate">
                      {topNetworkBundler.name}
                    </AddressLink>
                    <span className="text-xs text-gray-500">· {wc} wallet{wc === 1 ? '' : 's'} hold {pct.toFixed(1)}%</span>
                  </div>
                </div>
              );
            })()}

            {/* Stats grid (left) + funding-source anomaly chart (right).
                The anomaly chart replaces the old pie — it leads with
                the verdict (looks organic / unusual + warning text) and
                shows each exchange's deviation from baseline as a
                centered diff bar so weirdness is immediately visible. */}
            {tokenDetails.networkBundles && tokenDetails.individualHolders && (
              <div className="grid grid-cols-1 lg:grid-cols-[auto_minmax(0,1fr)] gap-2 items-start">
                <div className="bg-crypto-card border border-gray-800 rounded-xl p-3">
                  <div className="grid grid-cols-2 gap-2 lg:w-[280px]">
                    <StatCard label="Holders" value={(tokenDetails.totalHolders ?? 0).toLocaleString()} />
                    <StatCard label="Researched" value={`${(tokenDetails.walletsAnalyzed ?? 0).toLocaleString()}`} />
                    <StatCard label="Top holder" value={`${(tokenDetails.topHolderOwnership ?? 0).toFixed(1)}%`} valueColor={statColor(tokenDetails.topHolderRiskLevel)} />
                    <StatCard label="Top 10" value={`${(tokenDetails.topTenOwnership ?? 0).toFixed(1)}%`} valueColor={statColor(tokenDetails.topTenHoldersRiskLevel)} />
                    <StatCard label="Top network" value={topNetworkBundler ? `${(topNetworkBundler.percentOwnership ?? 0).toFixed(1)}%` : '—'} valueColor={topNetworkBundler ? statColor(topNetworkBundler.riskLevel) : undefined} />
                    <StatCard label="Networks" value={networkGroupCount.toString()} />
                    <StatCard
                      label="Held by ruggers"
                      value={tokenDetails.ruggerHoldingsPct != null ? `${tokenDetails.ruggerHoldingsPct.toFixed(1)}%` : '—'}
                      valueColor={
                        // Matches the safety tiers: ≥10% is a warning (RISKY) →
                        // red; 5–10% is info → yellow. (Warning cutoff lowered
                        // 15%→10% on 2026-07-27.)
                        tokenDetails.ruggerHoldingsPct == null ? undefined
                          : tokenDetails.ruggerHoldingsPct >= 10 ? 'text-crypto-red'
                          : tokenDetails.ruggerHoldingsPct >= 5 ? 'text-crypto-yellow'
                          : undefined
                      }
                    />
                    <StatCard label="Avg wallet age" value={formatWalletAge(tokenDetails.averageWalletAge)} />
                  </div>
                </div>
                <ExchangeAnomalyChart
                  individualHolders={tokenDetails.individualHolders}
                  networkBundles={tokenDetails.networkBundles}
                  warning={tokenDetails.exchangeDistributionWarning}
                />
              </div>
            )}

            {/* Streamflow locks and Tibane staking — self-hiding when
                no relevant data. Kept on this page (unlike V2 token
                page) because detailed lock/stake breakdown is a
                holder-page concern. */}
            {!chain && <StreamflowLocks mint={tokenDetails.mint} />}
            {!chain && <TibaneStaking mint={tokenDetails.mint} />}

            <BundleNetworks tokenDetails={tokenDetails as any} />
            <HoldersTable tokenDetails={tokenDetails as any} />
          </>
        )}
      </main>
      <Footer />

      <RefreshModal
        isOpen={refreshModalOpen}
        requestId={currentRequestId}
        onClose={handleModalClose}
        onSuccess={handleRefreshSuccess}
        onError={handleRefreshError}
      />
    </div>
  );
}

export default HolderAnalysisPage;
